import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiBaseUrl } from '../config/runtime';
import { getToken } from '../utils/auth';
import { downloadAuthedFile } from '../utils/downloadFile';
import AICreditGate from '../components/AICreditGate';
import {
  Shield, Star, Briefcase, AlertCircle,
  CheckCircle, ArrowLeft, RefreshCw, Lock, Download,
  ThumbsUp, AlertTriangle, Zap, Target, HelpCircle, MessageSquareQuote
} from 'lucide-react';

interface TargetUser {
  id: number; name: string; nickname: string; profile_image: string | null;
  rating: number | null; insurance_verified: number; insurance_verified_at: string | null;
  bio: string | null; title: string | null; skills: any;
  experience_years: number | null; hourly_rate: number | null;
}

const S = {
  page: { minHeight: '100vh', background: '#080808', color: '#fff', fontFamily: "-apple-system,'Pretendard',sans-serif", padding: '32px 24px' } as React.CSSProperties,
  container: { maxWidth: 720, margin: '0 auto' } as React.CSSProperties,
  card: { background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 16 } as React.CSSProperties,
  btn: (accent = '#a78bfa', disabled = false) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 28px', borderRadius: 10, border: 'none', background: accent, color: '#fff', fontSize: 15, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', width: '100%', opacity: disabled ? 0.6 : 1 } as React.CSSProperties),
};

const LEVEL_COLOR: Record<string, string> = { High: '#34d399', Medium: '#fbbf24', Low: '#f87171' };

/* ── 리포트 구조화 컴포넌트 ── */
const ReportContent: React.FC<{ report: any }> = ({ report }) => {
  if (!report) return null;
  const trustColor = LEVEL_COLOR[report.trustLevel] || '#a78bfa';
  return (
    <div>
      <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(255,255,255,0.04)', borderRadius: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: `${trustColor}22`, color: trustColor, fontWeight: 700 }}>{report.trustLevel}</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>활동도: {report.activityLevel}</span>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{report.summary}</div>
      </div>

      {report.taskFit?.enabled && (
        <div style={{ marginBottom: 16, padding: '14px 16px', background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>
            <Target size={14} /> 이 의뢰에 대한 적합도
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, marginBottom: 10 }}>{report.taskFit.overallAssessment}</div>
          {report.taskFit.matchedRequirements?.map((m: any, i: number) => (
            <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', padding: '4px 0' }}>
              <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{m.requirement}</strong> → {m.evidence} <span style={{ color: 'rgba(255,255,255,0.3)' }}>({m.confidence})</span>
            </div>
          ))}
          {report.taskFit.unmatchedRequirements?.length > 0 && (
            <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 6 }}>확인 불가: {report.taskFit.unmatchedRequirements.join(', ')}</div>
          )}
          {report.taskFit.suggestedQuestions?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                <HelpCircle size={12} /> 전문가에게 확인해보면 좋을 질문
              </div>
              {report.taskFit.suggestedQuestions.map((q: string, i: number) => (
                <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', padding: '2px 0' }}>· {q}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {report.strengths?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#34d399' }}><ThumbsUp size={14} /> 강점</div>
          {report.strengths.map((s: string, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}><CheckCircle size={13} color="#34d399" /> {s}</div>
          ))}
        </div>
      )}

      {report.concerns?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#fbbf24' }}><AlertTriangle size={14} /> 참고할 점</div>
          {report.concerns.map((c: string, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}><AlertCircle size={13} color="#fbbf24" /> {c}</div>
          ))}
        </div>
      )}

      {report.techStackDetected?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#a78bfa' }}><Zap size={14} /> GitHub 주요 언어</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {report.techStackDetected.map((t: string, i: number) => <span key={i} style={{ padding: '3px 10px', background: 'rgba(167,139,250,0.12)', color: '#a78bfa', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{t}</span>)}
          </div>
        </div>
      )}

      {report.reviewSummary && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}><MessageSquareQuote size={14} /> 리뷰 요약</div>
          <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{report.reviewSummary}</div>
        </div>
      )}

      {report.recommendation && (
        <div style={{ padding: '14px 16px', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 10, fontSize: 13, color: '#a78bfa', lineHeight: 1.6 }}>{report.recommendation}</div>
      )}

      {report._mock && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>
          {report._claudeApiKeyMissing ? 'Claude API 키가 설정되지 않아 예시 데이터입니다.' : '예시 데이터입니다.'}
        </div>
      )}
    </div>
  );
};

const ReportPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<any>(null);
  const [result, setResult] = useState<{ cacheId: number; report: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');
  const [requestText, setRequestText] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(apiBaseUrl(`/report/status/${userId}`), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!data.success) { setErr(data.message); return; }
      setStatus(data);
    } catch (e) { setErr('서버 오류가 발생했습니다.'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // 생성 요청 — 본인이 아니면 매 요청마다 이용권 1건 차감 (V4.0: 재조회 무료 로직 없음, 1회성 문서)
  const handleGenerate = async () => {
    setGenerating(true);
    setErr('');
    try {
      const token = getToken();
      const res = await fetch(apiBaseUrl(`/report/generate/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requestText: requestText.trim() || undefined, lang: 'ko' }),
      });
      const data = await res.json();
      if (!data.success) {
        setErr(data.message || '리포트 생성에 실패했습니다.');
        if (data.code === 'NO_CREDIT') await fetchStatus();
        return;
      }
      setResult({ cacheId: data.data.cacheId, report: data.data.report });
      await fetchStatus();
    } catch (e) {
      setErr('리포트 생성 중 오류가 발생했습니다.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      await downloadAuthedFile(`/api/report/download/${result.cacheId}`, `AI-report-${userId}.docx`);
    } catch (e: any) {
      setErr(e.message || '다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <RefreshCw size={24} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (err && !status) return (
    <div style={S.page}><div style={S.container}>
      <div style={{ ...S.card, textAlign: 'center', padding: 48 }}>
        <AlertCircle size={40} color="#f87171" style={{ marginBottom: 16 }} />
        <div style={{ color: '#f87171' }}>{err}</div>
        <button style={{ ...S.btn(), marginTop: 24, width: 'auto', padding: '10px 24px' }} onClick={() => navigate(-1)}><ArrowLeft size={16} /> 뒤로</button>
      </div>
    </div></div>
  );

  const targetUser: TargetUser | undefined = status?.targetUser;

  return (
    <div style={S.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={S.container}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}><ArrowLeft size={18} /> 뒤로</button>
          <div style={{ fontSize: 20, fontWeight: 800 }}>AI 전문가 분석 리포트</div>
        </div>

        {targetUser && (
          <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#a78bfa,#818cf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, flexShrink: 0, overflow: 'hidden' }}>
              {targetUser.profile_image ? <img src={targetUser.profile_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (targetUser.nickname || targetUser.name || '?')[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>
                {targetUser.nickname || targetUser.name}
                {targetUser.insurance_verified ? <span style={{ marginLeft: 8, fontSize: 11, background: '#065f46', color: '#6ee7b7', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>4대보험 인증</span> : null}
              </div>
              {targetUser.title && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{targetUser.title}</div>}
              <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
                {targetUser.rating !== null && <span style={{ fontSize: 12, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 3 }}><Star size={12} fill="#fbbf24" /> {Number(targetUser.rating).toFixed(1)}</span>}
                {targetUser.experience_years !== null && <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 3 }}><Briefcase size={12} /> {targetUser.experience_years}년</span>}
              </div>
            </div>
          </div>
        )}

        {/* 의뢰 내용(선택) */}
        {!status?.isSelf && (
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Target size={14} color="#a78bfa" /> 의뢰 내용 (선택)</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 10, lineHeight: 1.5 }}>
              맡기려는 작업 내용을 입력하면, 이 전문가가 그 작업을 얼마나 잘 해낼 수 있는지 적합도를 함께 분석합니다.
            </div>
            <textarea
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              placeholder="예: React + Node.js로 예약 관리 웹앱을 3주 안에 만들어줄 백엔드 개발자를 찾습니다..."
              rows={3}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: '#050505', color: '#fff', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        )}

        {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#f87171' }}>{err}</div>}

        {/* 생성 버튼 (매 요청마다 과금 — 본인 제외) */}
        <div style={S.card}>
          {status?.isSelf ? (
            <button style={S.btn('#a78bfa', generating)} onClick={handleGenerate} disabled={generating}>
              {generating ? '생성 중...' : 'AI 리포트 생성하기 (본인, 무료)'}
            </button>
          ) : (
            <AICreditGate toolType="report" toolLabel="AI 전문가 분석 리포트" creditsRemaining={status?.creditsRemaining ?? 0}>
              <button style={S.btn('#a78bfa', generating)} onClick={handleGenerate} disabled={generating}>
                {generating ? '생성 중... (최대 30초 정도 걸릴 수 있습니다)' : (result ? 'AI 리포트 다시 생성하기 (1건 차감)' : 'AI 리포트 생성하기 (1건 차감)')}
              </button>
            </AICreditGate>
          )}
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 10, textAlign: 'center' }}>
            생성할 때마다 새로운 워드(.docx) 문서로 제공됩니다. 재조회는 무료가 아닙니다.
          </div>
        </div>

        {/* 결과 */}
        {result && (
          <div style={{ ...S.card, borderColor: 'rgba(167,139,250,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <CheckCircle size={18} color="#a78bfa" />
              <span style={{ fontWeight: 700, fontSize: 15 }}>분석 결과</span>
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa', fontSize: 12, fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer' }}
              >
                <Download size={13} /> {downloading ? '다운로드 중...' : '워드 파일 다운로드'}
              </button>
            </div>
            <ReportContent report={result.report} />
          </div>
        )}

        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Shield size={16} color="#34d399" /><span style={{ fontWeight: 700, fontSize: 14 }}>4대보험 인증</span>
          </div>
          {targetUser?.insurance_verified ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><CheckCircle size={16} color="#34d399" /><span style={{ fontSize: 13, color: '#34d399' }}>인증 완료</span></div>
          ) : <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>미인증</div>}
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
